import { createRouter, createWebHistory } from 'vue-router';
import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { Role } from '@/constants/auth';
import { isPlanAllowed } from '@/utils/plan';

// 创建路由实例
const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginSaas.vue'),
    meta: {
      title: '登录',
      requiresAuth: false,
      hiddenLayout: true
    }
  },
  // 新增 SaaS 登录路由
  {
    path: '/login-saas',
    name: 'LoginSaas',
    component: () => import('@/views/LoginSaas.vue'),
    meta: {
      title: 'SaaS 登录',
      requiresAuth: false,
      hiddenLayout: true
    }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: {
      title: '注册',
      requiresAuth: false,
      hiddenLayout: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      title: '忘记密码',
      requiresAuth: false,
      hiddenLayout: true
    }
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    meta: {
      title: '重置密码',
      requiresAuth: false,
      hiddenLayout: true
    }
  },
  {
    path: '/',
    // 根路径处理 - 简化为直接重定向到登录页
    redirect: '/login',
    meta: { requiresAuth: false }
  },
  // 受保护的路由组 - 使用主布局
  {
    path: '/app',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
        // 仪表板
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            title: '仪表板',
            requiresAuth: true,
            icon: 'Odometer',
            breadcrumb: ['首页', '仪表板']
          }
        },
        {
          path: 'tenants',
          name: 'Tenants',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            title: '租户管理',
            requiresAuth: true,
            icon: 'OfficeBuilding',
            breadcrumb: ['系统管理', '租户管理']
          }
        },
        {
          path: 'devices',
          name: 'Devices',
          component: () => import('@/views/Devices.vue'),
          meta: {
            title: '设备管理',
            requiresAuth: true,
            icon: 'VideoCamera',
            breadcrumb: ['设备管理', '设备列表'],
            roles: [Role.Admin, Role.SysAdmin, Role.DeviceOps]
          }
        },
        {
          path: 'scenes',
          name: 'Scenes',
          component: () => import('@/views/Scenes.vue'),
          meta: {
            title: '场景管理',
            requiresAuth: true,
            icon: 'Collection',
            breadcrumb: ['场景管理', '场景列表']
          }
        },
        {
          path: 'deployments',
          name: 'Deployments',
          component: () => import('@/views/Deployments.vue'),
          meta: {
            title: '部署管理',
            requiresAuth: true,
            icon: 'Grid',
            breadcrumb: ['场景管理', '部署管理']
          }
        },
        {
          path: 'alerts',
          name: 'Alerts',
          component: () => import('@/views/Alerts.vue'),
          meta: {
            title: '告警管理',
            requiresAuth: true,
            icon: 'Bell',
            breadcrumb: ['告警管理', '告警列表']
          }
        },
        // 巡店整改任务（占位页，后续独立视图）
        {
          path: 'patrol-tasks',
           name: 'PatrolTasks',
           component: () => import('@/views/Tasks.vue'),
           meta: {
             title: '巡店整改任务',
             requiresAuth: true,
             icon: 'List',
             breadcrumb: ['运营管理', '巡店整改任务'],
             roles: [Role.Admin, Role.SysAdmin, Role.OpsSupervisor, Role.RegionalManager]
           }
         },
         {
           path: 'patrol-tasks/:id',
           name: 'PatrolTaskDetail',
           component: () => import('@/views/TaskDetail.vue'),
           meta: {
             title: '任务详情',
             requiresAuth: true,
             icon: 'List',
             breadcrumb: ['运营管理', '任务详情'],
             roles: [Role.Admin, Role.SysAdmin, Role.OpsSupervisor, Role.RegionalManager, Role.Operator, Role.StoreManager]
           }
         },
        // 新增：实时分析
        {
          path: 'realtime',
          name: 'Realtime',
          component: () => import('@/views/Realtime.vue'),
          meta: {
            title: '实时分析',
            requiresAuth: true,
            icon: 'VideoPlay',
            breadcrumb: ['视频分析', '实时分析']
          }
        },
        // 新增：AI巡店
        {
          path: 'ai-patrol',
          name: 'AIPatrol',
          component: () => import('@/views/AIPatrol.vue'),
          meta: {
            title: 'AI巡店',
            requiresAuth: true,
            icon: 'Cpu',
            breadcrumb: ['AI管理', 'AI巡店'],
            roles: [Role.Admin, Role.SysAdmin, Role.OpsDirector, Role.OpsSupervisor, Role.RegionalManager],
            requiredPlan: 'enterprise'
          }
        },
        {
          path: 'ai-patrol/:id',
          name: 'AIPatrolDetail',
          component: () => import('@/views/AIPatrolDetail.vue'),
          meta: {
            title: '巡店详情',
            requiresAuth: true,
            icon: 'Cpu',
            breadcrumb: ['AI管理', '巡店详情'],
            roles: [Role.Admin, Role.SysAdmin, Role.OpsSupervisor, Role.RegionalManager, Role.StoreManager],
            requiredPlan: 'enterprise'
          }
        },
        {
          path: 'ai-patrol/:id/replay',
          name: 'AIPatrolReplay',
          component: () => import('@/views/AIPatrolReplay.vue'),
          meta: {
            title: '巡店回放',
            requiresAuth: true,
            icon: 'VideoPlay',
            breadcrumb: ['AI管理', '巡店回放'],
            roles: [Role.Admin, Role.SysAdmin, Role.OpsSupervisor, Role.RegionalManager, Role.StoreManager],
            requiredPlan: 'enterprise'
          }
        },
        // 新增：数据报表
        {
          path: 'reports',
          name: 'Reports',
          component: () => import('@/views/Reports.vue'),
          meta: {
            title: '数据报表',
            requiresAuth: true,
            icon: 'DataLine',
            breadcrumb: ['统计分析', '数据报表'],
            // 角色可见性：系统管理员、运营总监、运营主管、区域店长、店长均可访问
            roles: [Role.Admin, Role.SysAdmin, Role.OpsDirector, Role.OpsSupervisor, Role.RegionalManager, Role.StoreManager],
            // 作用域：基于门店
            scope: 'store'
          }
        },
        {
          path: 'stores',
          name: 'Stores',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            title: '店铺管理',
            requiresAuth: true,
            icon: 'Shop',
            breadcrumb: ['基础数据', '店铺管理']
          }
        },
        {
          path: 'regions',
          name: 'Regions',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            title: '区域管理',
            requiresAuth: true,
            icon: 'MapLocation',
            breadcrumb: ['基础数据', '区域管理']
          }
        },
        {
          path: 'ai-models',
          name: 'AIModels',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            title: 'AI模型管理',
            requiresAuth: true,
            icon: 'Cpu',
            breadcrumb: ['AI管理', '模型管理']
          }
        },
        {
          path: 'users',
          name: 'Users',
          component: () => import('@/views/Users.vue'),
          meta: {
            title: '用户管理',
            requiresAuth: true,
            icon: 'User',
            breadcrumb: ['系统管理', '用户管理'],
            roles: [Role.Admin],
            permissions: ['manage_users']
          }
        },
        {
          path: 'profile',
          name: 'Profile',
          component: () => import('@/views/Profile.vue'),
          meta: {
            title: '个人中心',
            requiresAuth: true,
            breadcrumb: ['个人中心']
          }
        },
        {
          path: 'settings',
          name: 'Settings',
          component: () => import('@/views/Settings.vue'),
          meta: {
            title: '系统设置',
            requiresAuth: true,
            icon: 'Setting',
            breadcrumb: ['系统管理', '系统设置'],
            roles: [Role.Admin],
            permissions: ['system_settings']
          }
        }
      ]
    },
    {
      path: '/forbidden',
      name: 'Forbidden',
      component: () => import('@/views/Forbidden.vue'),
      meta: {
        title: '无权限访问',
        requiresAuth: false,
        hideLayout: true
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFound.vue'),
      meta: {
        title: '页面未找到',
        requiresAuth: false,
        hiddenLayout: true
      }
    }
];

// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 返回页面时保持滚动位置
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  let appStore: any;
  try {
    appStore = useAppStore();
  } catch (e) {
    // 在未初始化Pinia的测试环境下，提供空实现以避免报错
    appStore = { setPageTitle: () => {}, setBreadcrumb: () => {} };
  }

  // 设置页面标题
  if (to.meta.title) {
    appStore.setPageTitle(to.meta.title as string);
  }

  // 设置面包屑
  if (to.meta.breadcrumb) {
    appStore.setBreadcrumb(to.meta.breadcrumb as string[]);
  }

  // 初始化用户信息（如果未初始化）
  if (!userStore.isInitialized) {
    try {
      await userStore.initializeUser();
    } catch (error) {
      console.error('Failed to initialize user:', error);
      // 初始化失败但不影响后续流程，如果有有效token仍可继续
    }
  }

  // 1. 对于登录页（含 login-saas），如果已登录则重定向到dashboard
  if ((to.name === 'login' || to.name === 'LoginSaas') && userStore.isAuthenticatedRef.value) {
    next({ path: '/app/dashboard' });
    return;
  }

  // 2. 对于需要认证的路由
  if (to.meta.requiresAuth) {
    // 检查token是否过期（优先检查，避免重复判断）
    if (userStore.isTokenExpired) {
      // Token过期，清除用户信息并重定向到登录页
      await userStore.logout();
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }
    
    // 检查是否已登录（有有效token即可）
    if (!userStore.isAuthenticatedRef.value) {
      // 未登录，重定向到登录页
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }
    
    // 如果有token但还没有用户信息，后台异步加载（不阻塞页面）
    if (userStore.currentToken && !userStore.currentUser) {
      // 异步加载用户信息，不等待结果
      userStore.fetchCurrentUser().catch((error: any) => {
        console.error('异步加载用户信息失败:', error);
      });
    }

    // 认证通过后进行角色/权限校验（如果路由声明了要求）
    const requiredRoles = (to.meta.roles as string[]) || [];
    const requiredPermissions = (to.meta.permissions as string[]) || [];
    const roleOk = userStore.hasAnyRole ? userStore.hasAnyRole(requiredRoles) : requiredRoles.length === 0;
    const permOk = userStore.hasAnyPermission ? userStore.hasAnyPermission(requiredPermissions) : requiredPermissions.length === 0;

    // 调试日志：输出权限检查信息
    if (requiredRoles.length > 0 || requiredPermissions.length > 0) {
      console.log('[路由权限检查]', {
        path: to.path,
        requiredRoles,
        userRoles: userStore.roles?.value || [],
        roleOk,
        requiredPermissions,
        userPermissions: userStore.permissions?.value || [],
        permOk
      });
    }

    // 授权策略：满足角色或权限任一条件即可放行
    if (!(roleOk || permOk)) {
       console.warn('[路由权限拒绝]', { path: to.path, reason: '角色或权限不足' });
       next({ name: 'Forbidden' });
       return;
     }

    // 版本计划校验：当路由声明了 requiredPlan 时进行检查
    const requiredPlan = to.meta.requiredPlan as ('free' | 'enterprise' | 'custom') | undefined;
    if (requiredPlan && !isPlanAllowed((appStore.currentPlan?.value as any) || 'free', requiredPlan)) {
      next({ name: 'Forbidden' });
      return;
    }

     // 基于作用域的校验：门店
     const scopeType = to.meta.scope as string | undefined;
     if (scopeType === 'store') {
       const storeId = (to.query.storeId as string) || (to.params as any)?.storeId;
       const allowedList = userStore.allowedStoreIds?.value || [];
       if (storeId && !userStore.canAccessStore(storeId)) {
         if (allowedList.length > 0) {
           next({ name: to.name as any, params: to.params, query: { ...to.query, storeId: allowedList[0] } });
         } else {
           next({ name: 'Forbidden' });
         }
         return;
       }
       // 若未指定storeId且用户被分配了门店，则默认跳转到第一个门店
       if (!storeId && allowedList.length > 0) {
         next({ name: to.name as any, params: to.params, query: { ...to.query, storeId: allowedList[0] } });
         return;
       }
     }
  }
  
  // 3. 其他情况，允许导航
  next();
});

router.afterEach((to, from) => {
  // 关闭页面加载状态
  let appStore: any;
  try {
    appStore = useAppStore();
    appStore.setLoading(false);
  } catch (e) {
    // 测试环境未初始化Pinia时忽略加载状态
  }
  // 页面滚动到顶部
  window.scrollTo(0, 0);
  // 记录页面访问
  if (to.name && to.name !== from.name) {
    console.log(`路由跳转: ${from.name} -> ${to.name}`);
  }
});

export default router;
